import { z } from "zod"

import { climateZoneSchema } from "./climate-zone"
import { intervalInfoSchema } from "./interval-info"
import { propertyKeySchema } from "./property-key"

export const buildingTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  customerClass: z.string(),
})
export type Baseline = z.infer<typeof baselineSchema>

export const factorsSchema = z.object({
  peakDemand: z.number(),
  monthlyConsumption: z.number(),
  annualConsumption: z.number(),
  meanAnnualConsumption: z.number(),
  meanBuildingArea: z.number(),
  meanIntensity: z.number(),
  buildingArea: z.number().optional(),
})
export type Factors = z.infer<typeof factorsSchema>

export const measuresSchema = z.object({
  i: z.number(),
  v: z.number(),
})
export type Measures = z.infer<typeof measuresSchema>

export const baselineSchema = z.object({
  baselineId: z.string(),
  name: z.string(),
  buildingType: buildingTypeSchema,
  serviceType: z.string(),
  sourceId: z.string(),
  startDay: z.number(),
  climateZone: climateZoneSchema.optional(),
  properties: z.array(propertyKeySchema),
  factors: factorsSchema,
  measureDuration: z.number(),
  measureValue: z.string(),
  measureUnit: z.string(),
  /**
   * Populated only for a proportional (`groupBy`) baseline request. A dated
   * interval request — what Battery arbitrage sends — answers with `intervals`
   * and leaves this null, so the field cannot be required. Callers that need
   * the proportional measures should assert with `requireBaselineMeasures`
   * rather than treat an absent array as empty.
   */
  measures: z.array(measuresSchema).nullable(),
  intervals: z.array(intervalInfoSchema).optional(),
})
export type BuildingType = z.infer<typeof buildingTypeSchema>

export const DEFAULT_BASELINE: Baseline = {
  baselineId: ``,
  name: ``,
  buildingType: {
    id: ``,
    name: ``,
    description: ``,
    customerClass: ``,
  },
  serviceType: ``,
  sourceId: ``,
  startDay: 0,
  climateZone: {
    territoryId: 0,
    territoryName: ``,
    lseId: 0,
    lseName: ``,
    parentTerritoryId: 0,
    usageType: ``,
    itemTypes: ``,
    deregRes: false,
    deregCandi: false,
    centerPoint: {
      latitude: 0,
      longitude: 0,
    },
  },
  properties: [],
  factors: {
    peakDemand: 0,
    monthlyConsumption: 0,
    annualConsumption: 0,
    meanAnnualConsumption: 0,
    meanBuildingArea: 0,
    meanIntensity: 0,
    buildingArea: 0,
  },
  measureDuration: 0,
  measureValue: ``,
  measureUnit: ``,
  measures: [],
  intervals: [],
}

export const BASELINE_ZIPCODE = `94105`

/**
 * The proportional hourly measures, asserted present. Genability omits them
 * unless the request asked for them, and every caller of this needs them, so an
 * absent array is a broken response rather than a case to render around.
 */
export const requireBaselineMeasures = (baseline: Baseline): Measures[] => {
  const { measures } = baseline
  if (!measures) {
    throw new Error(
      `Genability baseline ${
        baseline.baselineId || `(unidentified)`
      } carries no hourly measures`,
    )
  }

  return measures
}
